import { useState, useEffect, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { getSettings, updateSettings, Settings } from '@/services/settings'
import { sendTestNewsletter } from '@/services/newsletter'
import { getSubscription } from '@/services/subscription'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/components/ui/use-toast'
import { Loader2, Plus, Trash2, Send, CheckCircle } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'

const settingsSchema = z.object({
  feeds: z
    .array(z.string().url({ message: 'URL de feed inválida.' }))
    .max(10, 'Máximo de 10 feeds.'),
  emails: z
    .array(z.string().email({ message: 'Email inválido.' }))
    .max(5, 'Máximo de 5 emails.'),
  send_time: z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/, {
    message: 'Formato de hora inválido.',
  }),
  prioritized_topics: z
    .array(z.string().min(1, 'Tópico não pode ser vazio.'))
    .max(20, 'Máximo de 20 tópicos.'),
})

const hourOptions = Array.from({ length: 24 }, (_, i) =>
  String(i).padStart(2, '0'),
)
const minuteOptions = Array.from({ length: 60 }, (_, i) =>
  String(i).padStart(2, '0'),
)

export default function DashboardPage() {
  const { toast } = useToast()
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [isSendingTest, setIsSendingTest] = useState(false)
  const [feedInput, setFeedInput] = useState('')
  const [emailInput, setEmailInput] = useState('')
  const [topicInput, setTopicInput] = useState('')
  const [subscription, setSubscription] = useState<any>(null)
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [selectedHour, setSelectedHour] = useState('09')
  const [selectedMinute, setSelectedMinute] = useState('00')

  const subscriptionStatus = searchParams.get('subscription')

  useEffect(() => {
    if (subscriptionStatus === 'success') {
      toast({
        title: 'Assinatura Ativada!',
        description: 'Obrigado por assinar o Briefly!',
      })
      navigate('/dashboard', { replace: true })
    }
  }, [subscriptionStatus, toast, navigate])

  const form = useForm<Settings>({
    resolver: zodResolver(settingsSchema),
    defaultValues: {
      feeds: [],
      emails: [],
      send_time: '09:00',
      prioritized_topics: [],
    },
  })

  const {
    watch,
    setValue,
    handleSubmit,
    formState: { isDirty, errors },
    reset,
  } = form
  const feeds = watch('feeds')
  const emails = watch('emails')
  const prioritized_topics = watch('prioritized_topics')

  useEffect(() => {
    if (selectedHour && selectedMinute) {
      setValue('send_time', `${selectedHour}:${selectedMinute}`, {
        shouldDirty: true,
      })
    }
  }, [selectedHour, selectedMinute, setValue])

  const loadData = useCallback(async () => {
    setIsLoading(true)
    try {
      const [settings, sub] = await Promise.all([
        getSettings(),
        getSubscription(),
      ])
      setSubscription(sub)
      if (settings) {
        reset(settings)
        const [hour, minute] = (settings.send_time || '09:00').split(':')
        setSelectedHour(hour || '09')
        setSelectedMinute(minute || '00')
      } else {
        const defaultSettings: Settings = {
          feeds: [],
          emails: [],
          send_time: '09:00',
          prioritized_topics: [],
        }
        await updateSettings(defaultSettings)
        reset(defaultSettings)
        setSelectedHour('09')
        setSelectedMinute('00')
      }
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Erro ao carregar dados.',
      })
    } finally {
      setIsLoading(false)
    }
  }, [reset, toast])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleAddItem = (
    type: 'feeds' | 'emails' | 'prioritized_topics',
    input: string,
    setInput: (val: string) => void,
  ) => {
    const currentItems = form.getValues(type) || []
    if (input && !currentItems.includes(input)) {
      const limits = { feeds: 10, emails: 5, prioritized_topics: 20 }
      if (currentItems.length < limits[type]) {
        setValue(type, [...currentItems, input.trim()], { shouldDirty: true })
        setInput('')
      } else {
        toast({
          variant: 'destructive',
          title: `Máximo de ${limits[type]} ${type === 'feeds' ? 'feeds' : type === 'emails' ? 'e-mails' : 'tópicos'} atingido.`,
        })
      }
    }
  }

  const handleRemoveItem = (
    type: 'feeds' | 'emails' | 'prioritized_topics',
    item: string,
  ) => {
    setValue(
      type,
      (form.getValues(type) || []).filter((i) => i !== item),
      { shouldDirty: true },
    )
  }

  const onSubmit = async (data: Settings) => {
    setIsSaving(true)
    try {
      await updateSettings(data)
      toast({ title: 'Configurações salvas com sucesso!' })
      reset(data)
    } catch (error) {
      toast({ variant: 'destructive', title: 'Erro ao salvar configurações.' })
    } finally {
      setIsSaving(false)
    }
  }

  const handleSendTest = async () => {
    if (!subscription) {
      toast({
        variant: 'destructive',
        title: 'Recurso Premium',
        description: 'Assine nosso plano para enviar newsletters de teste.',
      })
      return
    }
    setIsSendingTest(true)
    try {
      await sendTestNewsletter()
      toast({
        title: 'Newsletter de teste enviada!',
        description: 'Verifique sua caixa de entrada em alguns minutos.',
      })
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Falha ao enviar newsletter de teste.',
        description: error.message || 'Por favor, tente novamente.',
      })
    } finally {
      setIsSendingTest(false)
    }
  }

  if (isLoading) {
    return (
      <div className="container mx-auto max-w-4xl py-10 px-4 space-y-8">
        <Skeleton className="h-10 w-1/2" />
        <div className="space-y-4">
          <Skeleton className="h-8 w-1/4" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
        <div className="space-y-4">
          <Skeleton className="h-8 w-1/4" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
        <div className="space-y-4">
          <Skeleton className="h-8 w-1/4" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
        <Skeleton className="h-12 w-40" />
      </div>
    )
  }

  return (
    <div className="container mx-auto max-w-4xl py-10 px-4">
      <div className="flex justify-between items-start mb-8">
        <h1 className="text-3xl font-bold">Minhas Configurações</h1>
        {subscription && (
          <Badge variant="outline" className="border-green-500 text-green-400">
            <CheckCircle className="mr-2 h-4 w-4" />
            Plano Ativo
          </Badge>
        )}
      </div>

      {!subscription && (
        <Alert className="mb-8">
          <AlertTitle>Funcionalidades Premium</AlertTitle>
          <AlertDescription>
            Algumas funcionalidades, como o envio de newsletters de teste,
            requerem uma assinatura ativa.
          </AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-10">
        <div>
          <Label className="text-lg font-medium">Feeds RSS</Label>
          <p className="text-sm text-muted-foreground mb-4">
            Adicione até 10 URLs de feeds RSS.
          </p>
          <div className="flex gap-2 mb-2">
            <Input
              value={feedInput}
              onChange={(e) => setFeedInput(e.target.value)}
              placeholder="https://example.com/feed.xml"
            />
            <Button
              type="button"
              size="icon"
              onClick={() => handleAddItem('feeds', feedInput, setFeedInput)}
            >
              <Plus />
            </Button>
          </div>
          {errors.feeds && (
            <p className="text-sm text-destructive">{errors.feeds.message}</p>
          )}
          <div className="flex flex-wrap gap-2 mt-2">
            {feeds && feeds.length > 0 ? (
              feeds.map((feed) => (
                <Badge
                  key={feed}
                  variant="secondary"
                  className="flex items-center gap-2"
                >
                  {feed}
                  <button
                    type="button"
                    onClick={() => handleRemoveItem('feeds', feed)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </Badge>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">
                Nenhum feed RSS cadastrado ainda.
              </p>
            )}
          </div>
        </div>

        <div>
          <Label className="text-lg font-medium">
            E-mails para receber a newsletter
          </Label>
          <p className="text-sm text-muted-foreground mb-4">
            Adicione até 5 e-mails.
          </p>
          <div className="flex gap-2 mb-2">
            <Input
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              placeholder="email@exemplo.com"
            />
            <Button
              type="button"
              size="icon"
              onClick={() => handleAddItem('emails', emailInput, setEmailInput)}
            >
              <Plus />
            </Button>
          </div>
          {errors.emails && (
            <p className="text-sm text-destructive">{errors.emails.message}</p>
          )}
          <div className="flex flex-wrap gap-2 mt-2">
            {emails && emails.length > 0 ? (
              emails.map((email) => (
                <Badge
                  key={email}
                  variant="secondary"
                  className="flex items-center gap-2"
                >
                  {email}
                  <button
                    type="button"
                    onClick={() => handleRemoveItem('emails', email)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </Badge>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">
                Nenhum e-mail destinatário cadastrado ainda.
              </p>
            )}
          </div>
        </div>

        <div>
          <Label className="text-lg font-medium">Tópicos Prioritários</Label>
          <p className="text-sm text-muted-foreground mb-4">
            Adicione até 20 tópicos para priorizar no resumo.
          </p>
          <div className="flex gap-2 mb-2">
            <Input
              value={topicInput}
              onChange={(e) => setTopicInput(e.target.value)}
              placeholder="Ex: Inteligência Artificial"
            />
            <Button
              type="button"
              size="icon"
              onClick={() =>
                handleAddItem('prioritized_topics', topicInput, setTopicInput)
              }
            >
              <Plus />
            </Button>
          </div>
          {errors.prioritized_topics && (
            <p className="text-sm text-destructive">
              {errors.prioritized_topics.message}
            </p>
          )}
          <div className="flex flex-wrap gap-2 mt-2">
            {prioritized_topics && prioritized_topics.length > 0 ? (
              prioritized_topics.map((topic) => (
                <Badge
                  key={topic}
                  variant="secondary"
                  className="flex items-center gap-2"
                >
                  {topic}
                  <button
                    type="button"
                    onClick={() =>
                      handleRemoveItem('prioritized_topics', topic)
                    }
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </Badge>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">
                Nenhum tópico prioritário cadastrado ainda.
              </p>
            )}
          </div>
        </div>

        <div>
          <Label className="text-lg font-medium">Horário diário de envio</Label>
          <p className="text-sm text-muted-foreground mb-4">
            Escolha o horário para o envio diário.
          </p>
          <div className="flex items-center gap-2">
            <Select value={selectedHour} onValueChange={setSelectedHour}>
              <SelectTrigger className="w-[100px]">
                <SelectValue placeholder="Hora" />
              </SelectTrigger>
              <SelectContent>
                {hourOptions.map((hour) => (
                  <SelectItem key={hour} value={hour}>
                    {hour}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="font-medium">:</span>
            <Select value={selectedMinute} onValueChange={setSelectedMinute}>
              <SelectTrigger className="w-[100px]">
                <SelectValue placeholder="Minuto" />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                {minuteOptions.map((minute) => (
                  <SelectItem key={minute} value={minute}>
                    {minute}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {errors.send_time && (
            <p className="text-sm text-destructive mt-2">
              {errors.send_time.message}
            </p>
          )}
        </div>

        <div className="flex items-center gap-4 flex-wrap">
          <Button
            type="submit"
            disabled={!isDirty || isSaving || isSendingTest}
          >
            {isSaving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              'Salvar configurações'
            )}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleSendTest}
            disabled={isSaving || isSendingTest || !subscription}
            title={
              !subscription
                ? 'Assine para habilitar esta funcionalidade'
                : 'Enviar newsletter de teste'
            }
          >
            {isSendingTest ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" />
                Enviar Newsletter de Teste
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  )
}
